'use client';

import { useState, useEffect } from 'react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  getCountFromServer,
  query,
  where,
  orderBy,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from '@/lib/firebase-production.config';
import { useAuth } from '@/app/contexts/AuthContext';
import { Obra, ObraUser, Equipment, Task } from '@/types/obra';
import { UserProfile } from '@/types/user';

// Firestore values arrive untyped from doc.data(); this narrows a Timestamp |
// Date | date-string to a Date, or undefined.
const toDate = (value: unknown): Date | undefined => {
  const v = value as { toDate?: () => Date } | undefined;
  if (v?.toDate) return v.toDate();
  if (value) return new Date(value as string | number | Date);
  return undefined;
};

// Hook to get a single obra by ID
export function useObra(obraId: string | null | undefined) {
  const [obra, setObra] = useState<Obra | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!obraId) {
      setLoading(false);
      return;
    }

    // SSR guard - only run Firebase operations on client
    if (typeof window === 'undefined') {
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, 'obras', obraId),
      (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          const obraData: Obra = {
            id: doc.id,
            name: data.name,
            description: data.description,
            status: data.status,
            clientId: data.clientId,
            managerId: data.managerId,
            startDate: data.startDate?.toDate(),
            expectedEndDate: data.expectedEndDate?.toDate(),
            actualEndDate: data.actualEndDate?.toDate(),
            progress: data.progress || 0,
            sectors: data.sectors || [],
            elementos: data.elementos || [],
            plans: (data.plans || []).map((plan: Record<string, unknown>) => ({
              ...plan,
              uploadedAt: toDate(plan.uploadedAt),
              createdAt: toDate(plan.createdAt) || new Date(),
              updatedAt: toDate(plan.updatedAt) || new Date(),
            })),
            assignedUsers: data.assignedUsers || [],
            createdBy: data.createdBy,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
          };
          setObra(obraData);
          setError(null);
        } else {
          setObra(null);
          setError('Obra not found');
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching obra:', error);
        setError('Failed to fetch obra');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [obraId]);

  return { obra, loading, error };
}

// Hook to get user's assigned obras.
// `enabled` lets callers defer the (potentially large, admin-wide) subscription
// until it's actually needed — e.g. when an obra selector dropdown is opened.
export function useUserObras(options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  const { userProfile } = useAuth();
  const [obras, setObras] = useState<Obra[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    if (!userProfile) {
      setLoading(false);
      return;
    }

    // SSR guard - only run Firebase operations on client
    if (typeof window === 'undefined') {
      setLoading(false);
      return;
    }

    // Reset to loading whenever we (re)subscribe — e.g. when `enabled` flips
    // from false to true on first open — so consumers see an accurate state.
    setLoading(true);

    let unsubscribe: Unsubscribe;

    const fetchObras = async () => {
      try {
        // For admin users, get all obras
        if (userProfile.role === 'admin') {
          const q = query(collection(db, 'obras'), orderBy('updatedAt', 'desc'));
          unsubscribe = onSnapshot(q, (snapshot) => {
            const obrasData = snapshot.docs.map((doc) => {
              const data = doc.data();
              return {
                id: doc.id,
                name: data.name,
                description: data.description,
                status: data.status,
                clientId: data.clientId,
                managerId: data.managerId,
                startDate: data.startDate?.toDate(),
                expectedEndDate: data.expectedEndDate?.toDate(),
                actualEndDate: data.actualEndDate?.toDate(),
                progress: data.progress || 0,
                sectors: data.sectors || [],
                elementos: data.elementos || [],
                plans: (data.plans || []).map((plan: Record<string, unknown>) => ({
                  ...plan,
                  uploadedAt: toDate(plan.uploadedAt),
                  createdAt: toDate(plan.createdAt) || new Date(),
                  updatedAt: toDate(plan.updatedAt) || new Date(),
                })),
                assignedUsers: data.assignedUsers || [],
                createdBy: data.createdBy,
                createdAt: data.createdAt?.toDate() || new Date(),
                updatedAt: data.updatedAt?.toDate() || new Date(),
              } as Obra;
            });
            setObras(obrasData);
            setLoading(false);
          });
        } else {
          // For other users, get obras where they are assigned
          // Note: Removed orderBy to avoid requiring composite index
          const q = query(
            collection(db, 'obras'),
            where('assignedUsers', 'array-contains', userProfile.id)
          );

          unsubscribe = onSnapshot(q, (snapshot) => {
            console.log(
              '🔍 [DEBUG] useUserObras: Processing client snapshot with',
              snapshot.docs.length,
              'documents'
            );

            const obrasData = snapshot.docs.map((doc) => {
              const data = doc.data();

              return {
                id: doc.id,
                name: data.name,
                description: data.description,
                status: data.status,
                clientId: data.clientId,
                managerId: data.managerId,
                startDate: data.startDate?.toDate(),
                expectedEndDate: data.expectedEndDate?.toDate(),
                actualEndDate: data.actualEndDate?.toDate(),
                progress: data.progress || 0,
                sectors: data.sectors || [],
                elementos: data.elementos || [],
                plans: (data.plans || []).map((plan: Record<string, unknown>) => ({
                  ...plan,
                  uploadedAt: toDate(plan.uploadedAt),
                  createdAt: toDate(plan.createdAt) || new Date(),
                  updatedAt: toDate(plan.updatedAt) || new Date(),
                })),
                assignedUsers: data.assignedUsers || [],
                createdBy: data.createdBy,
                createdAt: data.createdAt?.toDate() || new Date(),
                updatedAt: data.updatedAt?.toDate() || new Date(),
              } as Obra;
            });

            // Sort client-side by updatedAt descending
            obrasData.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());

            setObras(obrasData);
            setLoading(false);
          });
        }
      } catch (error) {
        console.error('Error fetching user obras:', error);
        setError('Failed to fetch obras');
        setLoading(false);
      }
    };

    fetchObras();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [userProfile, enabled]);

  return { obras, loading, error };
}

// Lightweight count of obras visible to the current user, via Firestore's
// aggregation API (one cheap read instead of fetching every obra document).
// Used to decide whether to render the obra-switcher without eagerly loading
// the full list. Uses single-field queries only, so no composite index needed.
export function useUserObrasCount() {
  const { userProfile } = useAuth();
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    if (!userProfile || typeof window === 'undefined') {
      return;
    }

    let cancelled = false;

    const fetchCount = async () => {
      try {
        const obrasRef = collection(db, 'obras');
        const countQuery =
          userProfile.role === 'admin'
            ? query(obrasRef)
            : query(obrasRef, where('assignedUsers', 'array-contains', userProfile.id));
        const snapshot = await getCountFromServer(countQuery);
        if (!cancelled) {
          setCount(snapshot.data().count);
        }
      } catch (error) {
        console.error('Error counting user obras:', error);
        if (!cancelled) {
          setCount(null);
        }
      }
    };

    fetchCount();

    return () => {
      cancelled = true;
    };
  }, [userProfile]);

  return count;
}

// Hook to get workers assigned to an obra
export function useObraWorkers(obraId: string) {
  const [workers, setWorkers] = useState<UserProfile[]>([]);
  const [assignments, setAssignments] = useState<ObraUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!obraId) {
      setLoading(false);
      return;
    }

    const fetchWorkers = async () => {
      try {
        // First get obra assignments
        const assignmentsQuery = query(collection(db, 'obraUsers'), where('obraId', '==', obraId));

        const unsubscribeAssignments = onSnapshot(assignmentsQuery, async (snapshot) => {
          const assignmentsData = snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              userId: data.userId,
              obraId: data.obraId,
              role: data.role,
              assignedSectors: data.assignedSectors || [],
              assignedAt: data.assignedAt?.toDate() || new Date(),
              assignedBy: data.assignedBy,
            } as ObraUser;
          });

          setAssignments(assignmentsData);

          // Then fetch user details for each assignment
          if (assignmentsData.length > 0) {
            const userPromises = assignmentsData.map(async (assignment) => {
              const userDoc = await getDoc(doc(db, 'users', assignment.userId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                return {
                  id: userDoc.id,
                  email: userData.email,
                  displayName: userData.displayName,
                  photoURL: userData.photoURL,
                  role: userData.role,
                  companyId: userData.companyId,
                  bio: userData.bio,
                  title: userData.title,
                  phone: userData.phone,
                  active: userData.active ?? true,
                  emailVerified: userData.emailVerified ?? false,
                  lastLoginAt: userData.lastLoginAt?.toDate(),
                  createdAt: userData.createdAt?.toDate() || new Date(),
                  updatedAt: userData.updatedAt?.toDate() || new Date(),
                } as UserProfile;
              }
              return null;
            });

            const usersData = await Promise.all(userPromises);
            const validUsers = usersData.filter((user): user is UserProfile => user !== null);
            setWorkers(validUsers);
          } else {
            setWorkers([]);
          }

          setLoading(false);
        });

        return () => unsubscribeAssignments();
      } catch (error) {
        console.error('Error fetching obra workers:', error);
        setError('Failed to fetch obra workers');
        setLoading(false);
      }
    };

    const unsubscribe = fetchWorkers();
    return () => {
      if (unsubscribe) {
        // fetchWorkers resolves to undefined when it bails out or errors, so
        // calling unsub() unguarded threw on unmount in those cases.
        unsubscribe.then((unsub) => unsub?.());
      }
    };
  }, [obraId]);

  return { workers, assignments, loading, error };
}

// Hook to get equipment for an obra
export function useObraEquipment(obraId: string) {
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!obraId) {
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'equipment'),
      where('obraId', '==', obraId),
      orderBy('name', 'asc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const equipmentData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            obraId: data.obraId,
            name: data.name,
            description: data.description,
            type: data.type,
            brand: data.brand,
            model: data.model,
            serialNumber: data.serialNumber,
            status: data.status,
            currentLocation: data.currentLocation,
            assignedToUserIds:
              data.assignedToUserIds ?? (data.assignedToUserId ? [data.assignedToUserId] : []),
            assignedToUserId: data.assignedToUserId,
            purchaseDate: data.purchaseDate?.toDate(),
            warrantyExpiry: data.warrantyExpiry?.toDate(),
            maintenanceSchedule: data.maintenanceSchedule
              ? {
                  frequency: data.maintenanceSchedule.frequency,
                  lastMaintenance: data.maintenanceSchedule.lastMaintenance?.toDate(),
                  nextMaintenance: data.maintenanceSchedule.nextMaintenance?.toDate(),
                }
              : undefined,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
          } as Equipment;
        });

        setEquipment(equipmentData);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching obra equipment:', error);
        setError('Failed to fetch equipment');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [obraId]);

  return { equipment, loading, error };
}

// Hook to get tasks for an obra
export function useObraTasks(obraId: string, sectorId?: string) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!obraId) {
      setLoading(false);
      return;
    }

    let q = query(
      collection(db, 'tasks'),
      where('obraId', '==', obraId),
      orderBy('createdAt', 'desc')
    );

    // Filter by sector if provided
    if (sectorId) {
      q = query(
        collection(db, 'tasks'),
        where('obraId', '==', obraId),
        where('sectorId', '==', sectorId),
        orderBy('createdAt', 'desc')
      );
    }

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const tasksData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            obraId: data.obraId,
            sectorId: data.sectorId,
            planId: data.planId,
            elementoId: data.elementoId,
            title: data.title,
            description: data.description,
            status: data.status,
            priority: data.priority,
            assignedToUserIds:
              data.assignedToUserIds ?? (data.assignedToUserId ? [data.assignedToUserId] : []),
            assignedToUserId: data.assignedToUserId,
            createdBy: data.createdBy,
            estimatedHours: data.estimatedHours,
            actualHours: data.actualHours,
            startDate: data.startDate?.toDate(),
            dueDate: data.dueDate?.toDate(),
            completedDate: data.completedDate?.toDate(),
            dependencies: data.dependencies || [],
            equipmentIds: data.equipmentIds || [],
            notes: data.notes,
            attachments: data.attachments || [],
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
          } as Task;
        });

        setTasks(tasksData);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching obra tasks:', error);
        setError('Failed to fetch tasks');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [obraId, sectorId]);

  return { tasks, loading, error };
}

// Hook to check if user has access to an obra.
// Pass the already-loaded `obra` (and its `obraLoading` flag) — e.g. from
// ObraProvider — to reuse its assignedUsers instead of issuing a duplicate getDoc.
export function useObraAccess(obraId: string, obra?: Obra | null, obraLoading?: boolean) {
  const { userProfile } = useAuth();
  const [hasAccess, setHasAccess] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<string | null>(null);

  // Whether the caller is feeding us the obra (vs. legacy callers that pass only
  // obraId). When fed, we reuse its assignedUsers and never issue our own getDoc.
  const obraProvided = obra !== undefined;
  // Stable signal so the effect re-runs when membership actually changes, not on
  // every obra snapshot object identity.
  const cachedAssignedUsersKey =
    obra && obra.id === obraId ? (obra.assignedUsers || []).join(',') : null;

  useEffect(() => {
    if (!userProfile || !obraId) {
      setLoading(false);
      return;
    }

    const checkAccess = async () => {
      try {
        // Admin always has access
        if (userProfile.role === 'admin') {
          setHasAccess(true);
          setUserRole('admin');
          setLoading(false);
          return;
        }

        // Resolve the obra's assigned users. When the caller feeds the obra, wait
        // for it to load and reuse it (no duplicate read); otherwise read directly.
        let assignedUsers: string[];
        if (obraProvided) {
          if (cachedAssignedUsersKey === null) {
            // Obra fed but no matching loaded doc yet.
            if (obraLoading) {
              // Still loading — keep our loading true; re-run when it resolves.
              setLoading(true);
              return;
            }
            // Resolved to absent (not found / deleted) — deny access and stop
            // loading so the caller's own not-found UI can take over.
            setHasAccess(false);
            setLoading(false);
            return;
          }
          assignedUsers = cachedAssignedUsersKey ? cachedAssignedUsersKey.split(',') : [];
        } else {
          const obraDoc = await getDoc(doc(db, 'obras', obraId));
          if (!obraDoc.exists()) {
            setHasAccess(false);
            setLoading(false);
            return;
          }
          assignedUsers = obraDoc.data().assignedUsers || [];
        }

        {
          const isAssigned = assignedUsers?.includes(userProfile.id);

          if (isAssigned) {
            // Get user's role in this obra
            const assignmentQuery = query(
              collection(db, 'obraUsers'),
              where('obraId', '==', obraId),
              where('userId', '==', userProfile.id)
            );

            const assignmentSnapshot = await getDocs(assignmentQuery);
            if (!assignmentSnapshot.empty) {
              const assignmentData = assignmentSnapshot.docs[0].data();
              setUserRole(assignmentData.role);
            }

            setHasAccess(true);
          } else {
            setHasAccess(false);
          }
        }

        setLoading(false);
      } catch (error) {
        console.error('Error checking obra access:', error);
        setHasAccess(false);
        setLoading(false);
      }
    };

    checkAccess();
  }, [userProfile, obraId, obraProvided, cachedAssignedUsersKey, obraLoading]);

  return { hasAccess, userRole, loading };
}
